import { CornerStatusPill } from '@affine/core/components/corner-status-pill';
import { MailProviderKind } from '@affine/graphql';
import type { I18nInstance } from '@affine/i18n';
import { EmailIcon } from '@blocksuite/icons/rc';
import type { ReactNode } from 'react';

import mailboxOrgLogo from './assets/mailbox-org-logo.jpg';
import posteoLogo from './assets/posteo-logo.jpg';
import * as styles from './mail-fullscreen-view.css';
import { mailText } from './mail-i18n';
import {
  CUSTOM_MAIL_PROVIDER_OPTION_ID,
  MAIL_PASSWORD_PROVIDER_PRESETS,
  type MailPasswordProviderPreset,
  type MailProviderOptionId,
} from './provider-presets';

export const DEFAULT_PROVIDER_OPTION = CUSTOM_MAIL_PROVIDER_OPTION_ID;

export const MAIL_OAUTH_PROVIDER_LABELS: Partial<
  Record<MailProviderKind, string>
> = {
  [MailProviderKind.gmail_oauth]: 'Google',
  [MailProviderKind.microsoft_oauth]: 'Outlook / Microsoft 365',
};

type MailProviderMeta = {
  id: MailProviderOptionId;
  tileLabel: string;
  hint: string;
  tileLabelKey?: string;
  hintKey?: string;
  welcomeNameKey?: string;
  disabled?: boolean;
  comingSoon?: boolean;
  logo: ReactNode;
};

const MAIL_OAUTH_PROVIDER_META: MailProviderMeta[] = [
  {
    id: MailProviderKind.gmail_oauth,
    tileLabel: 'Google',
    hint: 'Gmail · Google Workspace',
    logo: <GoogleMark />,
  },
  {
    id: MailProviderKind.microsoft_oauth,
    tileLabel: 'Outlook',
    hint: 'Outlook · Microsoft 365',
    logo: <MicrosoftMark />,
  },
];

const MAIL_CUSTOM_PROVIDER_META: MailProviderMeta = {
  id: CUSTOM_MAIL_PROVIDER_OPTION_ID,
  tileLabel: 'Other',
  hint: 'Custom IMAP / SMTP',
  tileLabelKey: 'provider.custom.tileLabel',
  hintKey: 'provider.custom.hint',
  welcomeNameKey: 'welcome.provider.custom.name',
  logo: (
    <span className={styles.providerLogoGlyph}>
      <EmailIcon />
    </span>
  ),
};

const MAIL_PASSWORD_PROVIDER_META: MailProviderMeta[] =
  MAIL_PASSWORD_PROVIDER_PRESETS.map(provider => ({
    id: provider.id,
    tileLabel: provider.name,
    hint: provider.hint,
    logo: <ProviderLogo providerId={provider.id} label={provider.name} />,
  }));

const MAIL_PROVIDER_META_BY_ID = new Map<
  MailProviderOptionId,
  MailProviderMeta
>([
  ...MAIL_PASSWORD_PROVIDER_META.map(
    provider => [provider.id, provider] as const
  ),
  ...MAIL_OAUTH_PROVIDER_META.map(provider => [provider.id, provider] as const),
  [CUSTOM_MAIL_PROVIDER_OPTION_ID, MAIL_CUSTOM_PROVIDER_META] as const,
]);

const MAIL_WELCOME_PROVIDER_ORDER: readonly MailProviderOptionId[] = [
  'gmx',
  'web_de',
  MailProviderKind.microsoft_oauth,
  'mailbox_org',
  'posteo',
  'ionos',
  'mail_de',
  'freenet',
  'strato',
  MailProviderKind.gmail_oauth,
  CUSTOM_MAIL_PROVIDER_OPTION_ID,
];

export const MAIL_PROVIDER_META: MailProviderMeta[] =
  MAIL_WELCOME_PROVIDER_ORDER.map(id => {
    const provider = MAIL_PROVIDER_META_BY_ID.get(id);
    if (!provider) {
      throw new Error(`Missing mail provider metadata for ${id}`);
    }
    return provider;
  });

export function getMailProviderLogo(
  providerProfileId: string,
  providerKind?: MailProviderKind
): ReactNode {
  return (
    MAIL_PROVIDER_META_BY_ID.get(providerProfileId as MailProviderOptionId)
      ?.logo ??
    (providerKind
      ? MAIL_PROVIDER_META_BY_ID.get(providerKind as MailProviderOptionId)?.logo
      : undefined) ??
    MAIL_CUSTOM_PROVIDER_META.logo
  );
}

export function isMailOAuthProvider(
  providerKind: MailProviderKind
): providerKind is
  | MailProviderKind.gmail_oauth
  | MailProviderKind.microsoft_oauth {
  return (
    providerKind === MailProviderKind.gmail_oauth ||
    providerKind === MailProviderKind.microsoft_oauth
  );
}

export function getProviderTileLabel(
  provider: MailProviderMeta,
  i18n: I18nInstance
) {
  return provider.tileLabelKey
    ? mailText(i18n, provider.tileLabelKey)
    : provider.tileLabel;
}

export function getProviderHint(
  provider: MailProviderMeta,
  i18n: I18nInstance
) {
  return provider.hintKey ? mailText(i18n, provider.hintKey) : provider.hint;
}

export function ProviderComingSoonRibbon() {
  return (
    <CornerStatusPill
      className={styles.providerComingSoonRibbon}
      tone="warning"
      aria-hidden="true"
    >
      Coming soon
    </CornerStatusPill>
  );
}

export interface MailWelcomeProvider {
  id: MailProviderOptionId;
  name: string;
  hint: string;
  logo: ReactNode;
  disabled?: boolean;
  comingSoon?: boolean;
}

export function getMailWelcomeProviders(
  i18n: I18nInstance
): MailWelcomeProvider[] {
  return MAIL_PROVIDER_META.map(provider => ({
    id: provider.id,
    name: provider.welcomeNameKey
      ? mailText(i18n, provider.welcomeNameKey)
      : getProviderTileLabel(provider, i18n),
    hint: getProviderHint(provider, i18n),
    disabled: provider.disabled,
    comingSoon: provider.comingSoon,
    logo:
      provider.id === CUSTOM_MAIL_PROVIDER_OPTION_ID ? (
        <span className={styles.welcomeProviderGlyph}>
          <EmailIcon />
        </span>
      ) : (
        provider.logo
      ),
  }));
}

function GoogleMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M12 1h10v10H12z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" />
      <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  );
}

function ProviderLogo({
  providerId,
  label,
}: {
  providerId: MailPasswordProviderPreset['id'];
  label: string;
}) {
  switch (providerId) {
    case 'gmx':
      return (
        <SimpleProviderLogo
          label={label}
          color="#1652f0"
          path="M3.904 11.571v1.501H5.46c-.075.845-.712 1.274-1.539 1.274-1.255 0-1.934-1.157-1.934-2.3 0-1.118.65-2.317 1.906-2.317.77 0 1.321.468 1.586 1.166l1.812-.76C6.66 8.765 5.489 8.086 3.979 8.086 1.614 8.087 0 9.654 0 12.037c0 2.309 1.604 3.876 3.913 3.876 1.227 0 2.308-.439 3.025-1.44.651-.916.731-1.831.75-2.904zM13.65 8.3l-1.586 3.95-1.5-3.95H8.67l-1.255 7.392h1.91l.619-4.257h.019l1.695 4.257h.765l1.775-4.257h.024l.538 4.257h1.92L15.562 8.3zm7.708 3.473 2.086-3.475h-2.128l-1.11 1.767L19.012 8.3H16.68l2.459 3.47-2.46 3.922h2.333l1.33-2.223 1.576 2.223H24l-2.642-3.92"
        />
      );
    case 'mailbox_org':
      return <ProviderImageLogo label={label} src={mailboxOrgLogo} />;
    case 'ionos':
      return (
        <SimpleProviderLogo
          label={label}
          color="#001b41"
          path="M22.182 15.499a1.7634 1.7634 0 0 1-.3536-.0358 1.8319 1.8319 0 0 1-1.2086-.8632l-.0066-.012c-.1308-.2616-.0448-.5693.2-.7162a.5236.5236 0 0 1 .2689-.0743.5327.5327 0 0 1 .4632.272.558.558 0 0 0 .018.0299l.0564.0766.0757.0765a.7385.7385 0 0 0 .3453.167.7596.7596 0 0 0 .5954-.136.7206.7206 0 0 0 .299-.5386.7441.7441 0 0 0-.207-.578s-1.5994-1.6612-1.6712-1.7382a1.9604 1.9604 0 0 1-.1929-.229c-.2256-.3198-.2962-.697-.2614-1.0702a1.7361 1.7361 0 0 1 .2253-.7397s.0732-.124.0948-.1554c.0847-.1236.2515-.2834.3658-.3726a1.8813 1.8813 0 0 1 .3069-.1844 1.7494 1.7494 0 0 1 2.2815.7041.5186.5186 0 0 1 .0553.402.5298.5298 0 0 1-.2528.3263.5082.5082 0 0 1-.2593.0705.5283.5283 0 0 1-.46-.2706.6953.6953 0 0 0-.4656-.329.721.721 0 0 0-.5645.1269.688.688 0 0 0-.267.498.6998.6998 0 0 0 .1905.5398l1.6351 1.6862a1.8284 1.8284 0 0 1 .5071 1.3931c-.0465.5243-.3071.9994-.7152 1.3053a1.8425 1.8425 0 0 1-1.0984.369m-5.175-.0006a1.7608 1.7608 0 0 0 1.7585-1.7589v-3.4787a1.7587 1.7587 0 0 0-3.5173 0v3.4786a1.7608 1.7608 0 0 0 1.7587 1.7589m0-5.9342c.3777 0 .6968.319.6968.6967v3.4786a.6977.6977 0 0 1-.6968.6968.6894.6894 0 0 1-.6968-.6968v-3.4786a.6976.6976 0 0 1 .697-.6967m-7.8986.3224v5.0804a.5309.5309 0 0 0 1.0618 0V12.09Zm2.8802 2.0255V9.0328a.531.531 0 0 1 1.0619 0v5.0828zm1.053 2.9406v.1134a.533.533 0 0 1-1.0067.2408L9.1153 9.154v-.1212a.5261.5261 0 0 1 .3028-.4772.5253.5253 0 0 1 .7088.2475Zm-7.9067.6457a1.761 1.761 0 0 0 1.7586-1.7589v-3.4786a1.7587 1.7587 0 0 0-3.5173 0v3.4786a1.7609 1.7609 0 0 0 1.7587 1.7589m0-5.9342a.707.707 0 0 1 .6968.6967v3.4786a.6968.6968 0 1 1-1.3936 0v-3.4786a.6976.6976 0 0 1 .6968-.6967M0 14.9111a.5791.5791 0 1 0 1.1581 0V9.0803a.5791.5791 0 0 0-1.1582 0z"
        />
      );
    case 'mail_de':
      return <MailDeLogo label={label} />;
    case 'freenet':
      return (
        <SimpleProviderLogo
          label={label}
          color="#009fe3"
          path="M9.327 6.184c-.771-.17-1.577-.48-4.266-1.884C1.48 2.467.692 2.33.23 3.084c-.617.993-.051 3.512 2.415 8.103 1.594 3.015 4.112 6.904 4.574 7.418.103.102.223.137.326.068.103-.068.154-.154.069-.308-.377-.703-1.68-3.41-2.279-4.814-1.405-3.392-1.73-4.54-1.25-4.797.136-.068.308-.017.702.206 1.353.72 4.334 3.015 4.865 4.625.223.703.343 1.508.394 4.386.069 3.786.343 4.488 1.268 4.488 1.233 0 3.289-1.713 6.356-5.996 2.021-2.81 4.385-6.767 4.625-7.4.069-.138 0-.24-.086-.309-.102-.068-.222-.051-.308.086-.463.65-2.33 3.066-3.358 4.248-2.45 2.81-3.34 3.632-3.837 3.375-.137-.068-.171-.24-.171-.65 0-1.474.633-5.02 1.867-6.22.531-.53 1.25-1.027 3.906-2.5 3.512-1.954 4.009-2.519 3.546-3.29-.617-.993-3.22-1.799-8.771-2.124-3.615-.223-8.497-.137-9.2-.017-.154.017-.222.103-.24.222 0 .103.069.206.24.206.84.051 4.026.343 5.636.565 3.855.549 5.088.874 5.088 1.405 0 .137-.137.257-.53.463-1.388.788-5.02 2.038-6.75 1.661"
        />
      );
    case 'strato':
      return <StratoLogo label={label} />;
    case 'web_de':
      return (
        <ProviderWordmark label="WEB.DE" background="#ffd400" color="#111" />
      );
    case 'posteo':
      return <ProviderImageLogo label={label} src={posteoLogo} />;
    default:
      return (
        <ProviderWordmark label={label} background="#6b7280" color="#fff" />
      );
  }
}

function SimpleProviderLogo({
  label,
  color,
  path,
}: {
  label: string;
  color: string;
  path: string;
}) {
  return (
    <svg
      className={styles.providerLogoSvg}
      role="img"
      aria-label={label}
      viewBox="0 0 24 24"
    >
      <path fill={color} d={path} />
    </svg>
  );
}

function ProviderImageLogo({ label, src }: { label: string; src: string }) {
  return <img className={styles.providerLogoImage} src={src} alt={label} />;
}

function ProviderWordmark({
  label,
  background,
  color,
}: {
  label: string;
  background: string;
  color: string;
}) {
  return (
    <span
      className={styles.providerWordmark}
      style={{ background, color }}
      role="img"
      aria-label={label}
    >
      {label}
    </span>
  );
}

function MailDeLogo({ label }: { label: string }) {
  return (
    <svg
      className={styles.providerLogoSvg}
      role="img"
      aria-label={label}
      viewBox="0 0 294 180"
    >
      <path
        fill="#27a2f8"
        d="m195 169.9c-2.2 4.7-8.1 9-12.2 9.1H9c1.6 0-11.1.4-6.7-9L50.9 61.4c1.7-3.9 6.7-7.6 11-8.2l192.4-27.8c4.3-.7 5.9 3 3.6 8.2z"
      />
      <path
        fill="#002752"
        d="M112.7 154.3h-.5c-.8-.1-1.4-.6-1.8-1.3l-52-87.4c-.6-1.1-.5-2.2 0-3.1.6-1 1.6-1.5 2.6-1.5.7 0 1.7.4 2.2.9l52.3 50.3 171-108.1c.4-.3.9-.4 1.4-.4.8 0 1.6.4 2.2 1.1.8 1.1.6 2.7-.5 3.6L114.4 153.7c-.5.4-1.1.6-1.7.6"
      />
      <path
        fill="#27a2f8"
        d="M288 6.4 112.7 151.7l-52-87.4c-.2-.3.1-.6.4-.6.1 0 .2 0 .3.1l53.9 51.8L288 6.4M288 1c-.4 0-.8 0-1.2.1-.6.1-1.2.4-1.8.7l-169.1 107L65 59.9c-1-1-2.4-1.6-4-1.6-2 0-3.9 1.1-4.9 2.8-1 1.7-1.1 3.9-.1 5.7 0 .1.1.1.1.2l52 87.4c.8 1.3 2.1 2.3 3.7 2.5.3 0 .6.1.9.1 1.2 0 2.4-.4 3.4-1.2L291.3 10.6c1.3-1 2.1-2.5 2.1-4.2 0-2.9-2.3-5.3-5.3-5.4z"
      />
    </svg>
  );
}

function StratoLogo({ label }: { label: string }) {
  return (
    <span
      className={styles.providerLogoBadge}
      style={{ background: '#0050a4' }}
      role="img"
      aria-label={label}
    >
      <svg
        className={styles.providerLogoSvg}
        viewBox="0 0 112 112"
        aria-hidden="true"
      >
        <path
          fill="#fff"
          d="M55.6 15.3c-8.8 0-15.9-7.1-15.9-15.9h55.6c8.8 0 15.9 7.1 15.9 15.9v55.6c-8.8 0-15.9-7.1-15.9-15.9V23.3c0-4.4-3.6-7.9-7.9-7.9z"
        />
        <path
          fill="#fff"
          d="M79.4 39.1c0-4.4-3.6-7.9-7.9-7.9H7.9C3.6 31.2 0 34.8 0 39.1v63.5c0 4.4 3.6 7.9 7.9 7.9h63.5c4.4 0 7.9-3.6 7.9-7.9z"
        />
      </svg>
    </span>
  );
}
